import React, { useMemo, useState } from 'react';
import {
  Search,
  LayoutGrid,
  List,
  ArrowRight,
  FileText,
  Target,
  Lightbulb,
  TrendingUp,
  ClipboardCheck,
  Flame,
  Clock,
  RotateCcw,
  CalendarDays,
  MessageCircle
} from 'lucide-react';
import { UserProfile, PraxisPaper, TestAttempt } from '../types';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { ExamFamilyCard } from './ExamFamilyCard';
import { FamilyArt, getFamilyTheme, papersForFamily, paperTypeOf, paperHasContent, PaperTypeKey } from './brand/FamilyArt';

// =====================================================================
// Logged-in Exams Directory (2026-10-07 navy redesign). Replaces the old
// ExamCatalogOverview card grid on the "Exams Directory" tab. Everything
// shown is real: paper counts come from the live catalog, families with
// no practice content are marked "Coming soon", and the right-hand panel
// only uses the student's own profile and practice history (it is empty
// until they practice, never filled with sample numbers).
// =====================================================================

interface ExamsDirectoryProps {
  papers: PraxisPaper[];
  userProfile: UserProfile;
  testAttempts?: TestAttempt[];
  dueReviewCount?: number;
  onSelectExam: (entry: ExamCatalogEntry) => void;
  onViewAllIndividualPapers: () => void;
  onStartTest?: (paperId: string) => void;
  onOpenTutoring?: () => void;
  onOpenReview?: () => void;
}

export const NAVY_HERO_BG = 'radial-gradient(700px 300px at 85% 20%, #24398f 0%, transparent 60%), linear-gradient(120deg, #0c1640 0%, #0f1b4d 50%, #162a6b 100%)';

const TYPE_LABELS: { key: PaperTypeKey; label: string }[] = [
  { key: 'core', label: 'Core skills' },
  { key: 'elementary', label: 'Elementary' },
  { key: 'subject', label: 'Subject tests' },
  { key: 'plt', label: 'Teaching (PLT)' },
  { key: 'para', label: 'Paraprofessional' }
];

function formatDailyGoal(mins?: number): string {
  if (!mins || mins <= 0) return '';
  if (mins < 60) return `${mins} min/day`;
  if (mins === 60) return '1 hr/day';
  if (mins >= 180) return '3+ hrs/day';
  return `${Math.round(mins / 60)} hrs/day`;
}

function daysUntil(iso?: string | null): number | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((new Date(y, m - 1, d).getTime() - today.getTime()) / 86400000);
}

function relativeDay(iso: string): string {
  const t = Date.parse(iso);
  if (isNaN(t)) return '';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(t).toLocaleDateString();
}

export const ExamsDirectory: React.FC<ExamsDirectoryProps> = ({
  papers,
  userProfile,
  testAttempts = [],
  dueReviewCount = 0,
  onSelectExam,
  onViewAllIndividualPapers,
  onStartTest,
  onOpenTutoring,
  onOpenReview
}) => {
  const [stateQuery, setStateQuery] = useState('');
  const [types, setTypes] = useState<PaperTypeKey[]>([]);
  const [showLive, setShowLive] = useState(true);
  const [showSoon, setShowSoon] = useState(true);
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const familyPapers = useMemo(() => {
    const map: Record<string, PraxisPaper[]> = {};
    examCatalog.forEach((e) => { map[e.id] = papersForFamily(e, papers); });
    return map;
  }, [papers]);

  const typeCounts = useMemo(() => {
    const counts: Record<PaperTypeKey, number> = { core: 0, elementary: 0, subject: 0, plt: 0, para: 0 };
    const seen = new Set<string>();
    Object.values(familyPapers).flat().forEach((p) => {
      if (seen.has(p.id)) return;
      seen.add(p.id);
      counts[paperTypeOf(p)] += 1;
    });
    return counts;
  }, [familyPapers]);

  const totalPapers = useMemo(() => new Set(Object.values(familyPapers).flat().map((p) => p.id)).size, [familyPapers]);

  const visibleFamilies = examCatalog.filter((entry) => {
    const list = familyPapers[entry.id] || [];
    const live = list.length > 0;
    if (live && !showLive) return false;
    if (!live && !showSoon) return false;
    const q = stateQuery.trim().toLowerCase();
    if (q && !`${entry.state} ${entry.shortLabel} ${entry.examLabel} ${entry.examCodes.join(' ')}`.toLowerCase().includes(q)) return false;
    if (types.length > 0 && !list.some((p) => types.includes(paperTypeOf(p)))) return false;
    return true;
  });

  // ---- Goal panel (real profile data only) ----
  const goalPaper = userProfile.targetPaperId ? papers.find((p) => p.id === userProfile.targetPaperId) : undefined;
  const goalFamily = userProfile.targetExamFamily
    ? examCatalog.find((e) => e.id === userProfile.targetExamFamily)
    : goalPaper
    ? examCatalog.find((e) => (familyPapers[e.id] || []).some((p) => p.id === goalPaper.id))
    : examCatalog.find((e) => e.id === userProfile.targetPaperId);
  const daysLeft = daysUntil(userProfile.targetExamDate);
  const dailyGoal = formatDailyGoal(userProfile.dailyGoalMinutes);
  const hoursStudied = Math.round(((userProfile.totalStudyMinutes || 0) / 60) * 10) / 10;
  const recent = [...testAttempts]
    .filter((a) => a && a.completedAt)
    .sort((a, b) => Date.parse(b.completedAt) - Date.parse(a.completedAt))
    .slice(0, 3);

  const toggleType = (k: PaperTypeKey) => setTypes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const continueGoal = () => {
    if (goalPaper && paperHasContent(goalPaper) && onStartTest) {
      onStartTest(goalPaper.id);
    } else if (goalFamily && (familyPapers[goalFamily.id] || []).length > 0) {
      onSelectExam(goalFamily);
    }
  };

  return (
    <div className="space-y-5 animate-fadeIn">
      <div className="flex flex-col xl:flex-row gap-5">
        {/* ============ Main column ============ */}
        <div className="flex-1 min-w-0 space-y-5">
          {/* Hero */}
          <div className="relative overflow-hidden rounded-3xl text-white px-6 sm:px-8 py-7" style={{ background: NAVY_HERO_BG }}>
            <svg className="hidden md:block absolute right-8 top-5 w-[260px] h-[110px]" viewBox="0 0 300 120" fill="none" aria-hidden="true">
              <rect x="150" y="70" width="130" height="18" rx="4" fill="#fbbf24" opacity=".9" />
              <rect x="160" y="52" width="115" height="18" rx="4" fill="#f472b6" opacity=".9" />
              <rect x="155" y="34" width="122" height="18" rx="4" fill="#818cf8" />
              <rect x="60" y="40" width="70" height="48" rx="8" fill="#fff" opacity=".16" />
              <path d="M80 66l8 8 18-18" stroke="#a7f3d0" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="40" cy="22" r="6" fill="#fbbf24" opacity=".7" />
            </svg>
            <div className="relative z-10 max-w-2xl">
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-[11.5px] font-bold rounded-full px-3 py-1 bg-white/15 border border-white/20">Exams Directory</span>
                <span className="text-[11.5px] font-bold rounded-full px-3 py-1 bg-teal-300/15 border border-teal-300/40 text-teal-200">
                  ✓ {examCatalog.length} exam families · {totalPapers} papers
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Find your certification exam</h1>
              <p className="text-sm text-indigo-100/90 mt-2 leading-relaxed">
                Pick your state or exam family to see every paper we cover, with practice questions, mock exams, study guides and flashcards.
              </p>
            </div>
            <div className="relative z-10 mt-5 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-[12.5px] text-indigo-50">
                <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-teal-300" /> Official exam blueprints</span>
                <span className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-teal-300" /> Real exam format</span>
                <span className="flex items-center gap-2"><Lightbulb className="w-4 h-4 text-teal-300" /> Instant explanations</span>
                <span className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-teal-300" /> Progress tracking</span>
              </div>
              <button
                type="button"
                onClick={onViewAllIndividualPapers}
                className="lg:ml-auto shrink-0 bg-[#0E9594] hover:bg-[#0b7f7e] text-white font-extrabold text-[13px] rounded-xl px-4 py-2.5 flex items-center gap-1.5 transition cursor-pointer"
              >
                View all individual papers <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-5">
            {/* Filters */}
            <aside className="md:w-56 shrink-0 bg-white border border-slate-200 rounded-2xl p-4 self-start space-y-3">
              <h3 className="text-sm font-extrabold text-[#0f1b4d]">Narrow it down</h3>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={stateQuery}
                  onChange={(e) => setStateQuery(e.target.value)}
                  placeholder="Search by state..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#0E9594]/50"
                />
              </div>
              <div>
                <div className="text-xs font-extrabold text-slate-800 mb-2">Exam type</div>
                {TYPE_LABELS.map((t) => (
                  <label key={t.key} className="flex items-center gap-2.5 text-[12.5px] text-slate-700 py-1 cursor-pointer">
                    <input type="checkbox" checked={types.includes(t.key)} onChange={() => toggleType(t.key)} className="w-4 h-4 accent-[#0E9594]" />
                    <span>{t.label}</span>
                    <span className="ml-auto text-[11px] text-slate-400">{typeCounts[t.key]}</span>
                  </label>
                ))}
              </div>
              <div>
                <div className="text-xs font-extrabold text-slate-800 mb-2">Show</div>
                <label className="flex items-center gap-2.5 text-[12.5px] text-slate-700 py-1 cursor-pointer">
                  <input type="checkbox" checked={showLive} onChange={(e) => setShowLive(e.target.checked)} className="w-4 h-4 accent-[#0E9594]" />
                  Has practice content
                </label>
                <label className="flex items-center gap-2.5 text-[12.5px] text-slate-700 py-1 cursor-pointer">
                  <input type="checkbox" checked={showSoon} onChange={(e) => setShowSoon(e.target.checked)} className="w-4 h-4 accent-[#0E9594]" />
                  Coming soon
                </label>
              </div>
              <button
                type="button"
                onClick={() => { setStateQuery(''); setTypes([]); setShowLive(true); setShowSoon(true); }}
                className="w-full border border-slate-200 rounded-xl py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Reset filters
              </button>
            </aside>

            {/* Family grid / list */}
            <section className="flex-1 min-w-0 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-base font-extrabold text-[#0f1b4d]">Exam families</h3>
                <span className="text-xs text-slate-500 hidden sm:inline">{visibleFamilies.length} of {examCatalog.length} shown</span>
                <div className="ml-auto flex bg-slate-100 rounded-xl p-1 text-xs font-bold">
                  <button type="button" onClick={() => setView('grid')} className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 cursor-pointer ${view === 'grid' ? 'bg-[#0f1b4d] text-white' : 'text-slate-500'}`}>
                    <LayoutGrid className="w-3.5 h-3.5" /> Grid
                  </button>
                  <button type="button" onClick={() => setView('list')} className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 cursor-pointer ${view === 'list' ? 'bg-[#0f1b4d] text-white' : 'text-slate-500'}`}>
                    <List className="w-3.5 h-3.5" /> List
                  </button>
                </div>
              </div>

              {visibleFamilies.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-12">No exam family matches these filters.</div>
              ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-3">
                  {visibleFamilies.map((entry) => (
                    <ExamFamilyCard
                      key={entry.id}
                      entry={entry}
                      paperCount={(familyPapers[entry.id] || []).length}
                      isGoal={goalFamily?.id === entry.id}
                      showPills
                      onOpen={onSelectExam}
                    />
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
                  {visibleFamilies.map((entry) => {
                    const count = (familyPapers[entry.id] || []).length;
                    const theme = getFamilyTheme(entry);
                    const live = count > 0;
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        disabled={!live}
                        onClick={() => live && onSelectExam(entry)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${live ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-60 cursor-default'}`}
                      >
                        <span className="w-9 h-9 rounded-full text-white text-xs font-black flex items-center justify-center shrink-0" style={{ background: theme.dot }}>{theme.code}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-extrabold text-[#0f1b4d]">{entry.state}</span>
                          <span className="block text-[11.5px] text-slate-500 truncate">{theme.blurb || entry.examLabel}</span>
                        </span>
                        <FamilyArt entry={entry} className="hidden sm:block w-[90px] h-[38px] [&>svg]:w-full [&>svg]:h-full" />
                        <span className={`text-xs font-extrabold w-28 text-right ${live ? 'text-[#0E9594]' : 'text-slate-400'}`}>
                          {live ? `${count} papers →` : 'Coming soon'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>

        {/* ============ Right panel ============ */}
        <aside className="xl:w-[300px] shrink-0 space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="text-sm font-extrabold text-[#0f1b4d] mb-3">Your goal</h3>
            {goalPaper || goalFamily ? (
              <div className="rounded-xl p-4 text-white bg-gradient-to-br from-[#0f1b4d] to-[#1b2f7a] mb-3">
                <div className="text-[10.5px] font-extrabold tracking-widest text-teal-300 uppercase">
                  {goalPaper ? `${/^\d{4}$/.test(goalPaper.code) ? 'Praxis ' : ''}${goalPaper.code}` : goalFamily?.shortLabel}
                </div>
                <div className="text-sm font-extrabold mt-1 leading-snug">{goalPaper ? goalPaper.title : goalFamily?.examLabel}</div>
                {daysLeft !== null && daysLeft >= 0 ? (
                  <div className="flex items-baseline gap-1.5 mt-2">
                    <span className="text-3xl font-black text-teal-300">{daysLeft}</span>
                    <span className="text-xs text-indigo-100">
                      {daysLeft === 1 ? 'day' : 'days'} to your exam{dailyGoal ? ` · ${dailyGoal}` : ''}
                    </span>
                  </div>
                ) : (
                  <div className="text-xs text-indigo-100 mt-2 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {daysLeft !== null ? 'Your exam date has passed' : 'No exam date set yet'}
                    {dailyGoal ? ` · ${dailyGoal}` : ''}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl p-4 bg-slate-50 border border-dashed border-slate-300 text-xs text-slate-600 mb-3 flex items-start gap-2">
                <Target className="w-4 h-4 text-[#0E9594] shrink-0" />
                Pick an exam family below to start. Your goal and countdown will show here.
              </div>
            )}
            <div className="text-[12.5px] text-slate-600 divide-y divide-slate-100">
              <div className="flex items-center gap-2 py-2"><Flame className="w-4 h-4 text-amber-500" /> Study streak <b className="ml-auto text-slate-900">{userProfile.streakDays || 0} days</b></div>
              <div className="flex items-center gap-2 py-2"><Clock className="w-4 h-4 text-indigo-500" /> Time studied <b className="ml-auto text-slate-900">{hoursStudied} hrs</b></div>
              <button type="button" onClick={onOpenReview} className="w-full flex items-center gap-2 py-2 text-left cursor-pointer hover:text-slate-900">
                <RotateCcw className="w-4 h-4 text-pink-500" /> Questions due for review <b className="ml-auto text-slate-900">{dueReviewCount}</b>
              </button>
            </div>
            {(goalPaper || goalFamily) && (
              <button
                type="button"
                onClick={continueGoal}
                className="w-full mt-3 bg-[#0E9594] hover:bg-[#0b7f7e] text-white font-extrabold text-[13px] rounded-xl py-2.5 flex items-center justify-center gap-1.5 transition cursor-pointer"
              >
                Continue practicing <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="text-sm font-extrabold text-[#0f1b4d] mb-2">Recent practice</h3>
            {recent.length === 0 ? (
              <p className="text-xs text-slate-500 py-2">Your completed practice sessions will show here.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {recent.map((a) => (
                  <div key={a.id} className="flex items-center gap-2.5 py-2">
                    <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                      <ClipboardCheck className="w-3.5 h-3.5 text-[#0E9594]" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-slate-800 truncate">{a.paperCode} {a.mode === 'timed' ? 'Timed practice' : 'Practice'}</div>
                      <div className="text-[11px] text-slate-400">{relativeDay(a.completedAt)}</div>
                    </div>
                    <span className={`ml-auto text-xs font-extrabold ${a.percentage >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {Math.round(a.percentage)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="text-sm font-extrabold text-[#0f1b4d] mb-1.5">Need help choosing?</h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-3">Book a free consultation and we will match you to the right exam and plan.</p>
            <button
              type="button"
              onClick={onOpenTutoring}
              className="w-full bg-[#0f1b4d] hover:bg-[#162a6b] text-white font-extrabold text-[13px] rounded-xl py-2.5 flex items-center justify-center gap-1.5 transition cursor-pointer"
            >
              <MessageCircle className="w-4 h-4" /> Request a consultation
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};
